import { useToast } from '@retail/hooks';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import {
  useOverrideCampaignItemPrices,
  useResetCampaignItemPrice,
} from '@retail/campaign/data-access';
import { useInvalidateCampaignQueries } from './useInvalidateCampaignQueries';
import { useCallback, useMemo } from 'react';
import { CampaignItemPriceOverrideDTO } from '@retail/campaign/types';

interface Props {
  campaignId: string;
}

export const useOverrideCampaignItemPricesActions = ({ campaignId }: Props) => {
  const toast = useToast();
  const t = useCampaignTFunction();

  const { mutateAsync: mutateUpdateCampaignItem, isPending: isLoadingOverrideCampaignItem } =
    useOverrideCampaignItemPrices({ campaignId });

  const { mutateAsync: mutateResetCampaignItem, isPending: isLoadingResetCampaignItem } =
    useResetCampaignItemPrice({ campaignId });

  const invalidateCampaignQueries = useInvalidateCampaignQueries({ campaignId });

  const handleOverrideItemPrice = useCallback(
    async (id: string, item: CampaignItemPriceOverrideDTO) => {
      try {
        await mutateUpdateCampaignItem({
          urlVariables: id,
          body: item,
        });
        await invalidateCampaignQueries();
        toast.success(t('campaign.messages.itemUpdated'));
      } catch (error) {
        console.error(error);
        toast.error(t('campaign.error.genericError'));
      }
    },
    [invalidateCampaignQueries, mutateUpdateCampaignItem, t, toast]
  );

  const handleResetItemPrice = useCallback(
    async (id: string) => {
      try {
        await mutateResetCampaignItem({ urlVariables: id });
        await invalidateCampaignQueries();
        toast.success(t('campaign.messages.itemUpdated'));
      } catch (error) {
        console.error(error);
        toast.error(t('campaign.error.genericError'));
      }
    },
    [invalidateCampaignQueries, mutateResetCampaignItem, t, toast]
  );

  return useMemo(
    () => ({
      isLoadingOverrideCampaignItem,
      isLoadingResetCampaignItem,
      handleOverrideItemPrice,
      handleResetItemPrice,
    }),
    [
      isLoadingOverrideCampaignItem,
      isLoadingResetCampaignItem,
      handleOverrideItemPrice,
      handleResetItemPrice,
    ]
  );
};
